/**
 * <announcement-bar> — cycling announcement bar with fade/slide transitions.
 *
 * Loaded ONLY from `sections/section-announcement_bar.liquid` via
 * `type="module" fetchpriority="low"`. Does not live in global.js.
 *
 * Dependencies:
 *   - @theme/responsive
 */

import { responsive } from '@theme/responsive';

// Used by IntersectionObserver to track how much of the bar is in viewport.
const INTERSECTION_THRESHOLD_STEPS = 21;

// Horizontal travel before a drag counts as a swipe rather than a tap.
const SWIPE_THRESHOLD_PX = 40;

// Matches the 0.4s CSS exit transition on `.announcement-bar__block`
// (opacity + translateX) plus a 0.1s buffer.
const EXIT_TRANSITION_MS = 500;

const REVERSE_CLASS = 'announcement-bar__anim-wrapper--reverse';

function getVisibleElementHeight(element) {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  return Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
}

class AnimatedAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this._initialized = false;
    this._eventsBound = false;
    this._resizeListener = null;
    this._exitTransitionTimers = new Set();
    this._blocks = [];
    this._activeIndex = 0;
    this._pointerStart = null;
    this._swipeHandled = false;
    this._swipeBound = false;
    this._handlePointerDown = this._handlePointerDown.bind(this);
    this._handlePointerUp = this._handlePointerUp.bind(this);
    this._handlePointerCancel = this._handlePointerCancel.bind(this);
    this._handleClickCapture = this._handleClickCapture.bind(this);
  }

  _observeIntersection() {
    if (!this.isPrimaryAnnouncementBar()) return;

    this.intersectionObserver?.disconnect?.();
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this) return;
          this.updateVisibleHeight();
        });
      },
      {
        root: null,
        threshold: Array.from({ length: INTERSECTION_THRESHOLD_STEPS }, (_, i) => i / (INTERSECTION_THRESHOLD_STEPS - 1)),
      },
    );
    this.intersectionObserver.observe(this);
  }

  isPrimaryAnnouncementBar() {
    return this.tagName === 'ANNOUNCEMENT-BAR' && this.getAttribute('data-is-primary') === 'true';
  }

  scheduleAnnouncementHeightSync() {
    if (!this.isPrimaryAnnouncementBar()) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this._initialized) return;
        this.updateVisibleHeight();
      });
    });
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.initializeElements();
    if (!this.animWrapper || !this.original) return;

    this._wireImageMaskUrls();
    this.initializeAnimation();

    if (this.isPrimaryAnnouncementBar()) {
      document.documentElement.style.setProperty('--announcement-height-fixed', this.element.clientHeight + 'px');
      this.updateVisibleHeight();
      this._observeIntersection();
    }
    this.bindEvents();
  }

  disconnectedCallback() {
    this._resizeListener?.removeResizeListener();
    this._resizeListener = null;
    this._eventsBound = false;
    this.intersectionObserver?.disconnect();
    this.stopAnimation();
    this._initialized = false;
  }

  bindEvents() {
    if (this._eventsBound) return;
    this.handleBreakpointChange();
    // Use the shared, throttled resize manager rather than a raw
    // `window.addEventListener('resize')` so layout work batches across all
    // listeners on the page.
    this._resizeListener = responsive.addResizeListener(this, this.handleBreakpointChange);
    this._eventsBound = true;
  }

  // Wait for the image before enabling its color mask. Applying a background
  // while mask-image is still loading briefly paints the full image rectangle.
  _wireImageMaskUrls() {
    this.querySelectorAll('.announcement-bar__block-item img.image__element').forEach((img) => {
      const enableMask = () => {
        const src = img.currentSrc || img.src;
        if (!src) return;
        img.style.setProperty('--image-mask', `url("${src}")`);
        img.classList.add('announcement-bar__image--mask-ready');
        this.scheduleAnnouncementHeightSync();
      };

      if (img.complete && img.naturalWidth > 0) {
        enableMask();
      } else {
        img.addEventListener('load', enableMask, { once: true });
      }
    });
  }

  initializeElements() {
    const animOn = this.element.getAttribute('data-animation') === 'true';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.transitionSpeed = animOn && !prefersReducedMotion
      ? this.element.getAttribute('data-transition-speed')
      : null;

    this.animWrapper = this.querySelector('.announcement-bar__anim-wrapper');
    if (!this.animWrapper) {
      console.warn('AnnouncementBar: Missing .announcement-bar__anim-wrapper');
      return;
    }

    this.original = this.querySelector('.announcement-bar__duplicate-wrapper');
    if (!this.original) {
      console.warn('AnnouncementBar: Missing .announcement-bar__duplicate-wrapper');
    }
  }

  initializeAnimation() {
    this.hasSingleItem = (this.original?.children.length ?? 0) === 1;
    if (this.hasSingleItem) this.transitionSpeed = null;
  }

  stopAnimation() {
    if (this._slideCycleTimer) {
      clearInterval(this._slideCycleTimer);
      this._slideCycleTimer = null;
    }
    this._exitTransitionTimers.forEach((id) => clearTimeout(id));
    this._exitTransitionTimers.clear();
    this._unbindSwipe();
  }

  _applySlideCycleLayout() {
    this.stopAnimation();

    this.original.classList.remove('announcement-bar__duplicate-wrapper--space-between');
    this.original.classList.add('announcement-bar__duplicate-wrapper--centered');
    this.original.style.removeProperty('gap');
    this.original.style.removeProperty('width');
    this.original.style.removeProperty('margin');

    this._blocks = Array.from(this.original.children);
    this._blocks.forEach((block, i) => {
      block.classList.remove('announcement-bar__block--hidden', 'announcement-bar__block--exit');
      block.classList.toggle('announcement-bar__block--active', i === 0);
    });
    this._activeIndex = 0;

    this.animWrapper.classList.remove('announcement-bar__anim-wrapper--static', REVERSE_CLASS);
    this.animWrapper.classList.add('announcement-bar__anim-wrapper--slide-cycle');

    if (this._blocks.length <= 1) return;

    // `transition_speed` from the section schema is "seconds per full loop"
    // (legacy marquee semantics). For cycling we want "seconds per message" —
    // divide by 3 and clamp between 2s and 8s.
    const speedSec = parseInt(this.transitionSpeed, 10);
    this._dwellMs = Math.max(2, Math.min(8, Number.isFinite(speedSec) ? speedSec / 3 : 3)) * 1000;

    this._startCycleTimer();
    this._bindSwipe();
  }

  _startCycleTimer() {
    if (this._slideCycleTimer) clearInterval(this._slideCycleTimer);
    this._slideCycleTimer = window.setInterval(() => this._goToSlide(this._activeIndex + 1), this._dwellMs);
  }

  _goToSlide(index, { reverse = false } = {}) {
    const total = this._blocks.length;
    if (total <= 1) return;

    const nextIndex = ((index % total) + total) % total;
    if (nextIndex === this._activeIndex) return;

    // Parked blocks carry `transition: none`, so flipping the direction class
    // moves the incoming block to the opposite side instantly — but the browser
    // only picks that up as the transition's start value once layout is
    // recomputed. Without the forced reflow the block would still slide in from
    // its previous side.
    if (this.animWrapper.classList.contains(REVERSE_CLASS) !== reverse) {
      this.animWrapper.classList.toggle(REVERSE_CLASS, reverse);
      void this.animWrapper.offsetWidth;
    }

    const outgoing = this._blocks[this._activeIndex];
    const incoming = this._blocks[nextIndex];

    outgoing.classList.remove('announcement-bar__block--active');
    outgoing.classList.add('announcement-bar__block--exit');
    incoming.classList.add('announcement-bar__block--active');

    // Once the slide finishes we drop --exit so the block returns to its
    // off-screen "wait" position ready for its next turn. The id is tracked in
    // `this._exitTransitionTimers` so disconnectedCallback (via stopAnimation)
    // can cancel pending callbacks and avoid touching detached DOM nodes after
    // section re-render.
    const exitTimerId = window.setTimeout(() => {
      this._exitTransitionTimers.delete(exitTimerId);
      outgoing.classList.remove('announcement-bar__block--exit');
    }, EXIT_TRANSITION_MS);
    this._exitTransitionTimers.add(exitTimerId);

    this._activeIndex = nextIndex;
  }

  _bindSwipe() {
    if (this._swipeBound) return;
    this._swipeBound = true;

    this.animWrapper.addEventListener('pointerdown', this._handlePointerDown);
    this.animWrapper.addEventListener('pointerup', this._handlePointerUp);
    this.animWrapper.addEventListener('pointercancel', this._handlePointerCancel);
    this.animWrapper.addEventListener('click', this._handleClickCapture, true);
  }

  _unbindSwipe() {
    if (!this._swipeBound) return;
    this._swipeBound = false;
    this._pointerStart = null;
    this._swipeHandled = false;

    this.animWrapper.removeEventListener('pointerdown', this._handlePointerDown);
    this.animWrapper.removeEventListener('pointerup', this._handlePointerUp);
    this.animWrapper.removeEventListener('pointercancel', this._handlePointerCancel);
    this.animWrapper.removeEventListener('click', this._handleClickCapture, true);
  }

  _handlePointerDown(event) {
    if (!event.isPrimary) return;
    this._pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    this._swipeHandled = false;
  }

  _handlePointerUp(event) {
    const start = this._pointerStart;
    if (!start || event.pointerId !== start.id) return;
    this._pointerStart = null;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // Ignore drags that are mostly vertical — those are page scrolls that
    // happened to start on the bar.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

    this._swipeHandled = true;
    const reverse = dx > 0;
    this._goToSlide(this._activeIndex + (reverse ? -1 : 1), { reverse });
    // Restart the dwell so a manual swipe gets a full reading interval instead
    // of being cut short by a timer that was already halfway through.
    this._startCycleTimer();
  }

  _handlePointerCancel() {
    this._pointerStart = null;
  }

  // Blocks can wrap a link. A swipe that ends on one must not navigate.
  _handleClickCapture(event) {
    if (!this._swipeHandled) return;
    this._swipeHandled = false;
    event.preventDefault();
    event.stopPropagation();
  }

  updateVisibleHeight() {
    if (!this.isPrimaryAnnouncementBar()) return;

    document.documentElement.style.setProperty('--announcement-height-fixed', this.element.clientHeight + 'px');

    const visibleHeight = getVisibleElementHeight(this.element);
    if (this._lastAnnouncementDynamicHeight === visibleHeight) return;
    this._lastAnnouncementDynamicHeight = visibleHeight;
    document.documentElement.style.setProperty('--announcement-dynamic-height', `${visibleHeight}px`);
  }

  _applyNonAnimatedLayout() {
    const isDesktop = responsive.matchesBreakpoint('lap_and_up');
    const visibleCount = isDesktop ? 3 : 1;

    this.stopAnimation();

    this.original.classList.remove('announcement-bar__duplicate-wrapper--space-between');
    this.original.classList.add('announcement-bar__duplicate-wrapper--centered');
    // Desktop shows the 3 USPs side-by-side — give them a 1.5rem gap. Mobile shows
    // a single block, so the gap is irrelevant there (keep 0).
    this.original.style.setProperty('gap', isDesktop ? '1.5rem' : '0');
    this.original.style.setProperty('width', isDesktop ? '100%' : 'fit-content');
    this.original.style.setProperty('margin', isDesktop ? '0' : '0 auto');

    Array.from(this.original.querySelectorAll('.announcement-bar__block')).forEach((block, i) => {
      block.classList.toggle('announcement-bar__block--hidden', i >= visibleCount);
    });

    this.animWrapper.classList.remove('announcement-bar__anim-wrapper--slide-cycle');
    this.animWrapper.classList.add('announcement-bar__anim-wrapper--static');
  }

  handleBreakpointChange() {
    if (this.hasSingleItem) {
      this.stopAnimation();
      this.original.style.removeProperty('gap');
      this.original.style.removeProperty('width');
      this.original.style.removeProperty('margin');

      Array.from(this.original.children).forEach((block, i) => {
        block.classList.toggle('announcement-bar__block--hidden', i !== 0);
      });

      this.original.classList.remove('announcement-bar__duplicate-wrapper--space-between');
      this.original.classList.add('announcement-bar__duplicate-wrapper--centered');
      this.animWrapper.classList.remove('announcement-bar__anim-wrapper--slide-cycle');
      this.animWrapper.classList.add('announcement-bar__anim-wrapper--static');
      this.scheduleAnnouncementHeightSync();

      return;
    }

    if (responsive.matchesBreakpoint('lap_and_up')) {
      // Desktop is always static — shows up to 3 USPs side-by-side, never
      // animated regardless of the section's animation setting.
      this._applyNonAnimatedLayout();
    } else if (this.transitionSpeed) {
      // Mobile with animation: horizontal cycle — one group at a time,
      // outgoing slides out left while incoming arrives from the right.
      this._applySlideCycleLayout();
    } else {
      // Mobile without animation: a single visible group, no cycling.
      this._applyNonAnimatedLayout();
    }

    this.scheduleAnnouncementHeightSync();
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnimatedAnnouncementBar);
}
